import {compact, findLastIndex, isUndefined} from 'lodash';
import memoize from 'lru-memoize';

import {isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {maybeWrapInDoubleQuotes} from '../../../src/components/inboxes/search/hints/searchHintsHelpers';
import {
  isExistingModifier,
  isValidValueForModifier,
  ModifiersEnum,
  modifiersSet,
  TRANSIENT_MODIFIERS,
} from './searchModifiersHelpers';

/*
 * Types.
 */

interface SearchBaseToken {
  // `value` could be undefined, e.g. for `from:`.
  value: string | undefined;
}

export interface SearchModifierToken<Modifier extends ModifiersEnum = ModifiersEnum> extends SearchBaseToken {
  modifier: Modifier;
}

export type SearchToken<Modifier extends ModifiersEnum = ModifiersEnum> =
  | SearchBaseToken
  | SearchModifierToken<Modifier>;

export type SearchQueryPartText = {
  text: string;
  startOffset: number;
  endOffset: number;
  token: SearchBaseToken | undefined;
};
export type SearchQueryPartModifier<Modifier extends ModifiersEnum = ModifiersEnum> = {
  text: string;
  startOffset: number;
  endOffset: number;
  token: SearchModifierToken<Modifier>;
};

export type SearchQueryPart = SearchQueryPartText | SearchQueryPartModifier;

/*
 * Constants.
 */

const emptySearchQueryPart: SearchQueryPart = {
  text: '',
  startOffset: 0,
  endOffset: 0,
  token: {
    value: '',
  },
};

const WILDCARD_VALUE = '+';

/*
 * Helpers.
 */

/**
 * "                                # opening double-quote…
 * [^"]*                            # …followed by zero or more chars that are not a double-quote…
 * "?                               # …maybe closing double-quote
 */
const doubleQuotesRegExp = '"[^"]*"?';

/**
 * (?:${[...modifiers].join('|')})  # non-capturing group including one modifier
 * (?::\\s*)?                       # maybe non-capturing group including a colon followed by zero or more spaces
 * (                                # maybe non capturing group including:
 *  [^\\s"]+                        #   - anything that's not a space or a double-quote
 * |                                # or
 *  ${doubleQuotesRegExp}           #   - opening double-quote followed by zero or more chars that are not a double-quote and maybe closing double-quote
 * )?
 */
const modifierRegExp = `(?:${[...modifiersSet].join('|')})(?::\\s*)?(?:[^\\s"]+|${doubleQuotesRegExp})?`;

/**
 * /S+                              # anything that is not a whitespace
 */
const anythingButWhitespaceRegExp = '\\S+';

const searchQuerySplitRegExp = new RegExp(
  `(${modifierRegExp}|${doubleQuotesRegExp}|${anythingButWhitespaceRegExp})`,
);

/*
 * Type guards.
 */

export function isSearchModifierToken(
  token: SearchToken,
  hasTicketing = false,
): token is SearchModifierToken {
  return 'modifier' in token && isExistingModifier(token.modifier, hasTicketing);
}

const prefixTermSuffix = '*';
function isPrefixTermToken(token: SearchToken) {
  return (
    !isSearchModifierToken(token) &&
    token.value &&
    token.value !== prefixTermSuffix &&
    token.value.endsWith(prefixTermSuffix)
  );
}

export const negatedTermPrefix = '-';
function isNegatedTermToken(token: SearchToken) {
  return (
    !isSearchModifierToken(token) &&
    !isPrefixTermToken(token) && // Prefix terms have precedence over negated terms.
    token.value &&
    token.value !== negatedTermPrefix &&
    token.value.startsWith(negatedTermPrefix)
  );
}

/*
 * Helpers.
 */

export function splitSearchQuery(
  query: string,
  hasTicketingModifier = false,
): ReadonlyArray<SearchQueryPart> {
  let currentOffset = 0;
  const parts = query
    .split(searchQuerySplitRegExp)
    .filter((text) => text.length > 0)
    .map((text) => {
      const startOffset = currentOffset;
      const endOffset = currentOffset + text.length;
      currentOffset = endOffset;

      return buildQueryPart(text, startOffset, endOffset, hasTicketingModifier);
    });

  return parts.length > 0 ? parts : [emptySearchQueryPart];
}

export function buildQueryPart(
  text: string,
  startOffset = 0,
  endOffset = 0,
  hasTicketingModifier = false,
): SearchQueryPart {
  return {
    text,
    startOffset,
    endOffset,
    token: buildSearchToken(text, hasTicketingModifier),
  };
}

export function buildSearchToken(query: string, hasTicketingModifier = false): SearchToken | undefined {
  if (query.trim().length === 0) {
    return undefined;
  }

  if (!query.includes(':')) {
    return {value: query};
  }

  // Isolate potential modifier and value.
  const [modifier, value] = query.split(':');
  const lowerCasedModifier = modifier.toLowerCase();

  if (isExistingModifier(lowerCasedModifier, hasTicketingModifier)) {
    return {
      modifier: lowerCasedModifier,
      value: value.trim(),
    };
  }

  return {
    value: query,
  };
}

export function isWildcardToken(token: SearchToken, query?: string) {
  return (
    isUndefined(query) || !query.length || (!isSearchModifierToken(token) && token.value === WILDCARD_VALUE)
  );
}

export function isInvalidDotToken(token: SearchToken) {
  // When appended to a URL, the browser interprets those as current directory and previous directory.
  // This can lead to HTTP errors, we should skip them.
  return !isSearchModifierToken(token) && (token.value === '.' || token.value === '..');
}

export function findActiveToken(query: string, offset = 0): SearchToken | undefined {
  const searchQueryParts = splitSearchQuery(query);

  const activePartIndex = searchQueryParts.findIndex((part) =>
    Boolean(part.startOffset <= offset && offset <= part.endOffset),
  );

  // `token` does not exist in a search query part only if we're matching on space(s).
  // If this is the case, try to fall back to the next search query part, or the previous one.
  return (
    searchQueryParts[activePartIndex]?.token ||
    searchQueryParts[activePartIndex + 1]?.token ||
    searchQueryParts[activePartIndex - 1]?.token
  );
}

export const findMemoizedActiveToken = memoize()(findActiveToken);

export function findActiveTokens(query: string, offset = 0): ReadonlyArray<SearchToken> {
  const searchQueryParts = splitSearchQuery(query);

  const activePartIndex = searchQueryParts.findIndex((part) =>
    Boolean(part.startOffset <= offset && offset <= part.endOffset),
  );

  // `token` does not exist in a search query part only if we're matching on space(s).
  // If this is the case, try to fall back to the next search query part, or the previous one.
  const activeToken: SearchToken | undefined =
    searchQueryParts[activePartIndex]?.token ||
    searchQueryParts[activePartIndex + 1]?.token ||
    searchQueryParts[activePartIndex - 1]?.token;

  if (!activeToken) {
    return [];
  }

  // If the active token is a modifier one, return it.
  if (isSearchModifierToken(activeToken)) {
    return [activeToken];
  }

  // Otherwise, find all adjacent tokens including the active one that are not modifier ones.
  const preQueryParts = searchQueryParts.slice(0, activePartIndex);
  const lastPreModifierTokenIndex = findLastIndex(
    preQueryParts,
    (part) => isDefined(part.token) && isSearchModifierToken(part.token),
  );

  const postQueryParts = searchQueryParts.slice(activePartIndex + 1);
  const firstPostModifierTokenIndex = postQueryParts.findIndex(
    (part) => isDefined(part.token) && isSearchModifierToken(part.token),
  );

  const activeQueryParts = searchQueryParts.slice(
    lastPreModifierTokenIndex + 1,
    firstPostModifierTokenIndex === -1 ? undefined : activePartIndex + firstPostModifierTokenIndex + 1,
  );

  const activeTokens = compact(activeQueryParts.map((part) => part.token));
  return activeTokens.length ? activeTokens : [activeToken];
}

export const findMemoizedActiveTokens = memoize()(findActiveTokens);

export function isValidSearchModifierToken(token: SearchModifierToken): boolean {
  const {modifier, value} = token;

  return isValidValueForModifier(modifier, value);
}

export const isModifierQueryPart = <Modifier extends ModifiersEnum>(
  part: SearchQueryPart,
  modifier: Modifier,
): part is SearchQueryPartModifier<Modifier> =>
  isDefined(part.token) && isSearchModifierToken(part.token) && part.token.modifier === modifier;

export const appendSearchToken = (
  queryParts: ReadonlyArray<SearchQueryPart>,
  searchToken: SearchToken,
): ReadonlyArray<SearchQueryPart> => {
  if (!searchToken.value) {
    return queryParts;
  }

  const newQueryParts = [...queryParts];
  // if last element is not a white space, push a white space
  if (newQueryParts.length > 0 && newQueryParts[newQueryParts.length - 1].text !== ' ') {
    newQueryParts.push(buildQueryPart(' '));
  }

  const newModifierValuePart = buildQueryPart(
    isSearchModifierToken(searchToken)
      ? `${searchToken.modifier}:${maybeWrapInDoubleQuotes(searchToken.value)}`
      : searchToken.value,
  );
  newQueryParts.push(newModifierValuePart);

  return newQueryParts;
};

export const toggleModifierValue = (
  queryParts: ReadonlyArray<SearchQueryPart>,
  modifier: ModifiersEnum,
  value: string,
) => {
  let newQueryParts: ReadonlyArray<SearchQueryPart> = [...queryParts];
  const selectedPart = queryParts.find(
    (e) => isModifierQueryPart(e, modifier) && e.token.value === maybeWrapInDoubleQuotes(value),
  );
  const valueWasSelected = Boolean(selectedPart);

  if (!valueWasSelected) {
    if (TRANSIENT_MODIFIERS.includes(modifier) && queryParts.some((e) => e.text === value)) {
      const enabledText = `${modifier}:${maybeWrapInDoubleQuotes(value)}`;
      newQueryParts = queryParts.map((part) => (part.text === value ? buildQueryPart(enabledText) : part));
    } else {
      newQueryParts = appendSearchToken(queryParts, {modifier, value});
    }
  } else {
    newQueryParts = queryParts.filter((e) => e !== selectedPart);
  }
  return {
    queryParts: newQueryParts,
    valueWasSelected,
  };
};

export function buildSearchQueryFromParts(parts: ReadonlyArray<SearchQueryPart>): string {
  return parts
    .map((part) => part.text)
    .join('')
    .trim();
}

export const getModifierPartsFromSearchQuery = <Modifier extends ModifiersEnum>(
  query: string,
  modifier: Modifier,
) =>
  splitSearchQuery(query).filter((part): part is SearchQueryPartModifier<Modifier> =>
    isModifierQueryPart(part, modifier),
  );

export function removeModifierValueFromParts(parts: ReadonlyArray<SearchQueryPart>, modifier: ModifiersEnum) {
  return parts.filter((part) => !isModifierQueryPart(part, modifier));
}

function isNonNegatedTermToken(token: SearchToken) {
  return !isSearchModifierToken(token) && token.value && !token.value.startsWith(negatedTermPrefix);
}

export function computeIsNonNegatedTermPresent(queryParts: ReadonlyArray<SearchQueryPart>) {
  return queryParts.some(({token}) => token && isNonNegatedTermToken(token));
}

export function computeIsNegatedTermPresent(queryParts: ReadonlyArray<SearchQueryPart>) {
  return queryParts.some(({token}) => token && isNegatedTermToken(token));
}

export function wrapNegatedTermsInQuotes(queryParts: ReadonlyArray<SearchQueryPart>) {
  return queryParts.map((part) =>
    part.token && isNegatedTermToken(part.token)
      ? {...part, text: `"${part.text}"`, token: {...part.token, value: `"${part.token.value}"`}}
      : part,
  );
}
